import { useEffect } from "react";
import {
  browserReportsKeyboard,
  forgetTouches,
  KEYBOARD_MIN,
  keyboardCovering,
  noteTouch,
  TYPED_INTO,
  visibleBand,
} from "../../../lib/viewport";

/**
 * Keeps the field somebody is typing in above the on-screen keyboard.
 *
 * A phone keyboard does not make the page shorter. It slides over the bottom
 * of it — the *visual* viewport shrinks while the layout viewport the page was
 * laid out in stays exactly as tall as it was. So a field near the foot of the
 * screen is still there, still focused, still receiving the letters being
 * typed, and covered by the keyboard the whole time. Browsers do make one
 * attempt to scroll it clear, but it is a minimal one: the field lands flush
 * against the keyboard's top edge when it lands at all, and it does not land
 * at all when the page has nothing left to scroll.
 *
 * That last case is the one this is really for, and it is why padding is half
 * the fix. The public form screens are `min-h-screen` and no more — a short
 * step fits the window exactly, the document has no overflow, and no amount of
 * scrolling moves anything. `--keyboard-inset` opens exactly as much room
 * below the page as the keyboard is covering, so there is somewhere to scroll
 * to; see `PublicFormShell`, which is what spends it.
 *
 * The other half is the scroll itself: enough to clear the keyboard and a
 * little more, so the field sits above it with air under it rather than
 * balanced on its edge — which is what a native app does, and what makes the
 * difference between reading as deliberate and reading as luck.
 *
 * Nothing here runs on a desktop: a window with no keyboard over it has no gap
 * to report and no glass to type on, so the hook measures once on a resize and
 * stops.
 *
 * How much is being covered is `visibleBand`'s to work out, and it is not
 * always the browser that says — the in-app browsers a shared link opens in
 * report no keyboard at all, which is why a form filled in from Instagram or
 * Facebook used to leave the field being typed into behind one. What this hook
 * owes that reading is the two things only a listener can see: the caret
 * moving, and where fingers land. The second is what tells a browser that
 * never mentioned its keyboard that the keyboard has gone.
 */

/** Air left between the bottom of the field and the top of the keyboard. */
const GAP_BELOW = 24;

/**
 * And above it — enough for the field's own label, which is the part that says
 * what is being asked. A field pinned to the very top of the strip left over
 * is legible and anonymous at the same time.
 */
const GAP_ABOVE = 72;

/**
 * Long enough for the keyboard to have finished sliding up. Browsers report
 * the resize as it starts as well as when it ends, and the first figure is the
 * height the keyboard was passing through rather than the one it stopped at.
 */
const SETTLE_MS = 280;

/**
 * Whether the field is pinned to the viewport rather than laid out on the
 * page — the filter box at the head of a dropdown's sheet, which is the one
 * control on either form that is.
 *
 * Scrolling cannot move it, so nothing below has anything to correct: the box
 * is above the keyboard by construction, and a page scrolled on its behalf
 * would only drag the form along underneath it for no reason. Read off the
 * computed position rather than off a marker attribute, because the fact that
 * matters here is exactly that one.
 */
const isPinned = (node: HTMLElement): boolean => {
  for (let element: HTMLElement | null = node; element; element = element.parentElement) {
    if (window.getComputedStyle(element).position === "fixed") return true;
  }
  return false;
};

const useKeyboardAvoidance = (): void => {
  useEffect(() => {
    const viewport = window.visualViewport;
    const root = document.documentElement;
    let frame = 0;
    let timer: number | undefined;
    /** When the caret last arrived somewhere — see `touched` below. */
    let focusedAt = 0;

    /**
     * @param moveField whether the field being typed into may be scrolled to
     *                  where it belongs, as well as the room below the page
     *                  being brought up to date.
     *
     *                  False for anything a finger caused. A drag *is* the
     *                  reader scrolling, and a correction landing in the middle
     *                  of one is the page pulling against the thumb — which on
     *                  iOS, where every touch already re-reads the keyboard, is
     *                  the whole of why scrolling a form with the keyboard up
     *                  stopped feeling smooth. What a touch proves about the
     *                  keyboard is still worth having; the scroll that would
     *                  answer it is not.
     */
    const settle = (moveField: boolean) => {
      // What the keyboard is covering: the difference between the page's own
      // viewport and the strip of it still on screen. Reported by the browser
      // where the browser reports it, and assumed where it does not — which is
      // the in-app browsers a shared link is most often opened in, and the
      // whole reason this reading lives in `visibleBand` rather than here.
      const band = visibleBand();
      // Asked for rather than worked out from the band: on iOS the two are
      // different questions, and the strip's bottom edge is no measure of the
      // keyboard under it. See `keyboardCovering`.
      const keyboard = keyboardCovering();
      const open = keyboard >= KEYBOARD_MIN;

      root.style.setProperty(
        "--keyboard-inset",
        open ? `${keyboard}px` : "0px"
      );

      if (!open || !moveField) return;

      const field = document.activeElement;
      if (!(field instanceof HTMLElement) || !field.matches(TYPED_INTO)) return;
      if (isPinned(field)) return;

      const rect = field.getBoundingClientRect();
      const { top, bottom } = band;

      /**
       * Whether this browser has already scrolled for its own keyboard.
       *
       * The ones that report a keyboard also act on it: they bring the focused
       * field into the strip that is left, and they land it flush against the
       * keys. Asking for another two dozen pixels on top of that is a second
       * scroll chasing the browser's own — and on iOS, where every tap on a
       * field triggers Safari's version first, that second scroll is the judder
       * that comes with entering a value or moving to the next box.
       *
       * So where the browser has done the work, this only steps in for a field
       * genuinely still under the keyboard, and never to fetch a label back
       * into view. Where nothing has scrolled at all — the in-app browsers,
       * which do not know there is a keyboard — both corrections stand, because
       * there is nothing to fight with.
       */
      const scrolledAlready = browserReportsKeyboard();

      // How far the field reaches past the keyboard's edge, and how far it
      // sits above the headroom its label wants. Only one of the two can be a
      // positive number.
      const under = rect.bottom - (bottom - (scrolledAlready ? 0 : GAP_BELOW));
      const over = scrolledAlready ? 0 : top + GAP_ABOVE - rect.top;

      // Up to clear the keyboard, or back down to bring the label into view —
      // and the second only as far as it can go without putting the field back
      // under the keyboard, so the two corrections cannot argue with each
      // other for the rest of the sitting.
      const move = under > 0 ? under : over > 0 ? -Math.min(over, -under) : 0;

      // A pixel or two is not worth a scroll, and asking for one on every
      // keystroke's worth of resize is how a page ends up shivering.
      if (Math.abs(move) < 2) return;

      window.scrollBy({
        top: move,
        // The motion is what says the page moved rather than that the field
        // jumped somewhere else — unless the reader has asked for less of it.
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
      });
    };

    // Once on the next frame for the keyboard that is already up — moving
    // between two fields resizes nothing — and once more after it has finished
    // opening, for the one that is still on its way.
    const schedule = (moveField = true) => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => settle(moveField));
      window.clearTimeout(timer);
      timer = window.setTimeout(() => settle(moveField), SETTLE_MS);
    };

    // Absent in the odd webview, and nothing here needs it: `focusin` alone is
    // enough to catch a keyboard the browser never announced.
    /**
     * Where a finger lands, which in the browsers that report nothing is the
     * only news there is that a keyboard has gone back down — see `noteTouch`.
     * The reading is taken again straight after, so the room a keyboard no
     * longer needs is given back under the touch that proved it rather than
     * whenever something else happens to ask.
     *
     * `touchmove` as well as `touchstart`: a drag that begins above the
     * keyboard and ends below where its top edge was proves the same thing,
     * and a scroll is how most people touch a form they have finished typing
     * into. Passive, because neither is ever cancelled here.
     */
    const touched = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch) return;
      // Not while a keyboard is still on its way up. For the length of that
      // slide there is screen below the field that is about to be covered and
      // is not covered yet, and a finger that lands there in the meantime
      // would prove the opposite of what is happening — collapsing the room
      // the keyboard is about to need, and putting the field back under it.
      if (Date.now() - focusedAt < SETTLE_MS) return;
      // The room below the page is brought up to date; the field is left
      // exactly where the thumb has put it. See `settle`.
      if (noteTouch(touch.clientY)) schedule(false);
    };

    /** An event that says the keyboard may have changed, not a finger. */
    const recheck = () => schedule();

    const focused = () => {
      focusedAt = Date.now();
      forgetTouches();
      schedule();
    };

    viewport?.addEventListener("resize", recheck);
    window.addEventListener("touchstart", touched, { passive: true });
    window.addEventListener("touchmove", touched, { passive: true });
    // On the window rather than the field, so every control on every step is
    // covered without one of them having to remember to ask.
    //
    // And what fingers have proved is forgotten here: the caret arriving in a
    // field is the moment a keyboard may be about to come up, and the tap that
    // put it there is very often the deepest touch of the sitting.
    window.addEventListener("focusin", focused);
    // And out again, which is the only news that a keyboard nobody reported
    // has gone back down. Without it the room made for one would stay open
    // under a form that no longer has anything to type into — a screen's worth
    // of blank page below the last question, and the bar at the foot of it
    // stranded off the bottom of the screen.
    window.addEventListener("focusout", recheck);

    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      viewport?.removeEventListener("resize", recheck);
      window.removeEventListener("touchstart", touched);
      window.removeEventListener("touchmove", touched);
      window.removeEventListener("focusin", focused);
      window.removeEventListener("focusout", recheck);
      root.style.removeProperty("--keyboard-inset");
    };
  }, []);
};

export default useKeyboardAvoidance;
